import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { AudioPicker } from "@/features/audio-picker";
import { Hint } from "@/features/form-hint";
import { LineSkillsField } from "@/features/line-skills";
import { StageRulesField } from "@/features/stage-rules";
import { ApiError } from "@/lib/api";
import { RINGBACK_US, isTone } from "@/lib/audio";
import { mediaProblem } from "@/lib/campaign";
import {
  LINE_BOUNDS, LINE_PURPOSES, RING_STRATEGIES, SKILL_STRATEGIES, lineChanges, linePurposeText,
  lineValues, newLineValues, purposeTaken, ringStrategyText, skillStrategyText, skillsForPurpose,
  type LineValues,
} from "@/lib/line";
import { useFormChanged, zodResolver } from "@/lib/form";
import { lineSummary } from "@/lib/skills";
import { stageRuleProblem } from "@/lib/stage-rules";
import { useAddLine, useUpdateLine, type LineSettingsInput } from "@/lib/queries";
import type { Campaign, Queue } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * One line's settings, added or edited (ADR-014). The dialog beside `campaign-lines.tsx`, which
 * lists the lines and opens this over them: the form is long enough — the skills, the stage rules,
 * four timings and what a caller hears — that it reads better in a file of its own than under the
 * table it is opened from.
 */
interface LineForm extends LineValues {
  purpose: string;
  name: string;
}

function whole([min, max]: readonly [number, number], unit = "seconds") {
  return z
    .number({ error: `Enter a number of ${unit}` })
    .int(`Whole ${unit} only`)
    .min(min, `At least ${min} ${unit}`)
    .max(max, `At most ${max.toLocaleString()} ${unit}`);
}

const media = z.string().refine((v) => mediaProblem(v) === null, "Choose a file from the audio store");

const lineSchema = z.object({
  purpose: z.string(),
  // `_LINE_NAME_RE` in core-api's schemas. Empty lets core-api name it `<slug>-<purpose>`.
  name: z
    .string()
    .regex(/^([a-z0-9][a-z0-9_-]{0,62})?$/, "Lowercase letters, digits, underscore and hyphen, up to 63"),
  description: z.string().max(200, "At most 200 characters"),
  ring_timeout_seconds: whole(LINE_BOUNDS.ring_timeout_seconds),
  ring_strategy: z.string(),
  skill_strategy: z.string(),
  required_skills: z.array(z.object({ slug: z.string(), min_level: z.number() })),
  skill_relax_seconds: whole(LINE_BOUNDS.skill_relax_seconds),
  previous_rep_first: z.boolean(),
  wrap_up_seconds: whole(LINE_BOUNDS.wrap_up_seconds),
  disposition_seconds: whole(LINE_BOUNDS.disposition_seconds),
  max_wait_seconds: whole(LINE_BOUNDS.max_wait_seconds),
  default_priority: whole(LINE_BOUNDS.default_priority, "points"),
  entry_announcement: media,
  moh: media,
  enabled: z.boolean(),
  // Each rule says what is wrong with it beside itself; this only keeps a broken one from saving.
  stage_rules: z
    .array(
      z.object({
        stage: z.string(),
        mode: z.enum(["only", "first"]),
        skills: z.array(z.object({ slug: z.string(), min_level: z.number() })),
        fallback_seconds: z.number().nullable(),
        dialer: z.boolean().optional(),
      }),
    )
    .refine((rules) => rules.every((r) => stageRuleProblem(r) === null), "Fix the stage rules above."),
});


/** The three things a line can play to a caller who is waiting: the switch's own music, a ringing
 *  tone (a client asked for one — a caller who hears ringing does not think they were forgotten),
 *  or a file from the audio store. The value is hold media either way, so the choice is only what
 *  is stored: "" for the switch's music, which core-api puts back as `local_stream://moh`. */
function HoldMedia({
  id, value, onChange,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const choice = value === "" ? "music" : isTone(value) ? "tone" : "file";
  // What was picked before, so switching to Ringing tone and back does not lose the file.
  const lastFile = React.useRef(choice === "file" ? value : "");
  if (choice === "file") lastFile.current = value;

  return (
    <div className="space-y-2">
      <div className="grid gap-2 sm:grid-cols-3">
        {HOLD_MEDIA_CHOICES.map((c) => (
          <label
            key={c.key}
            className="flex cursor-pointer items-start gap-2.5 rounded-md border border-border px-3 py-2.5 has-[:checked]:border-accent-8 has-[:checked]:bg-accent-2 has-[:disabled]:cursor-default"
          >
            <input
              type="radio"
              name="line-hold-media"
              id={c.key === "music" ? id : undefined}
              className="mt-0.5 accent-accent-9"
              checked={choice === c.key}
              onChange={() =>
                onChange(c.key === "music" ? "" : c.key === "tone" ? RINGBACK_US : lastFile.current)
              }
            />
            <span className="min-w-0">
              <span className="block text-xs font-medium text-text">{c.label}</span>
              <span className="block text-xs text-text-muted">{c.description}</span>
            </span>
          </label>
        ))}
      </div>
      {choice === "file" && (
        <AudioPicker
          value={value}
          onChange={onChange}
          emptyLabel="No file chosen yet"
        />
      )}
    </div>
  );
}

const HOLD_MEDIA_CHOICES: readonly { key: string; label: string; description: string }[] = [
  { key: "music", label: "Hold music", description: "The music the switch plays." },
  { key: "tone", label: "Ringing tone", description: "It rings until someone answers." },
  { key: "file", label: "A file", description: "One of your own, on a loop." },
];

export function LineDialog({
  open, onOpenChange, campaign, line,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: Campaign;
  /** Absent to add a line. */
  line?: Queue;
}) {
  const toast = useToast();
  const add = useAddLine();
  const update = useUpdateLine();
  const [formError, setFormError] = React.useState<string | null>(null);
  const firstFree = LINE_PURPOSES.find((p) => !purposeTaken(p, campaign.lines)) ?? "custom";

  const form = useForm<LineForm>({
    resolver: zodResolver(lineSchema),
    defaultValues: { ...newLineValues(firstFree), purpose: firstFree, name: "" },
  });
  const { errors } = form.formState;
  const changed = useFormChanged(form);

  React.useEffect(() => {
    if (!open) return;
    setFormError(null);
    form.reset(
      line
        ? { ...lineValues(line), purpose: line.purpose ?? "custom", name: line.name }
        : { ...newLineValues(firstFree), purpose: firstFree, name: "" },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, line]);

  const purpose = form.watch("purpose");
  const ringStrategy = form.watch("ring_strategy");
  const skillStrategy = form.watch("skill_strategy");
  const primary = line?.purpose === "primary";
  const summary = lineSummary({
    required: form.watch("required_skills"),
    ring: ringStrategyText(ringStrategy).label,
    order: ringStrategy === "ring_all" ? null : skillStrategyText(skillStrategy).label,
    ringSeconds: form.watch("ring_timeout_seconds"),
  });

  const onSubmit = form.handleSubmit(async ({ purpose: chosen, name, ...settings }) => {
    setFormError(null);
    try {
      if (line) {
        const changes = lineChanges(settings, line) as LineSettingsInput;
        if (Object.keys(changes).length > 0) await update.mutateAsync({ id: line.id, ...changes });
        toast.success("Line saved", line.name);
      } else {
        const created = await add.mutateAsync({
          campaignId: campaign.id,
          purpose: chosen,
          name: name || null,
          ...(lineChanges(settings) as LineSettingsInput),
        });
        toast.success("Line added", created.name);
      }
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not save the line.");
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>{line ? "Line settings" : "Add a line"}</DialogTitle>
          <DialogDescription>
            {line ? (
              <>
                How calls wait on <span className="font-mono">{line.name}</span>.
              </>
            ) : (
              `A second line for ${campaign.name}, for calls that need settings of their own.`
            )}
          </DialogDescription>
        </DialogHeader>

        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}

            <SectionHeading first>Basics</SectionHeading>

            {!line && (
              <>
                <Field
                  label="Purpose"
                  htmlFor="line-purpose"
                  required
                  hint={linePurposeText(purpose).description}
                >
                  <Select
                    value={purpose}
                    onValueChange={(v) => {
                      // A closer line needs `closer` unless the admin has picked skills of their own.
                      form.setValue(
                        "required_skills",
                        skillsForPurpose(purpose, v, form.getValues("required_skills")),
                        { shouldDirty: true },
                      );
                      form.setValue("purpose", v);
                    }}
                  >
                    <SelectTrigger id="line-purpose">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LINE_PURPOSES.map((p) => {
                        const taken = purposeTaken(p, campaign.lines);
                        return (
                          <SelectItem
                            key={p}
                            value={p}
                            disabled={taken}
                            description={taken ? "This campaign has one already." : linePurposeText(p).description}
                          >
                            {linePurposeText(p).label}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </Field>
                <Field
                  label="Name"
                  htmlFor="line-name"
                  error={errors.name?.message}
                  hint={<Hint more="What a number routes to and what reps sign in to.">Cannot be changed later.</Hint>}
                >
                  <Input
                    className="font-mono"
                    autoComplete="off"
                    placeholder={`${campaign.slug}-${purpose}`}
                    invalid={Boolean(errors.name)}
                    {...form.register("name")}
                  />
                </Field>
              </>
            )}

            <Field label="Description" htmlFor="line-description" error={errors.description?.message}>
              <Input
                autoComplete="off"
                invalid={Boolean(errors.description)}
                {...form.register("description")}
              />
            </Field>

            <Field
              label="Ring"
              htmlFor="line-ring-strategy"
              hint={ringStrategyText(ringStrategy).description}
            >
              <Select
                value={ringStrategy}
                onValueChange={(v) => form.setValue("ring_strategy", v)}
              >
                <SelectTrigger id="line-ring-strategy">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RING_STRATEGIES.map((s) => (
                    <SelectItem key={s} value={s} description={ringStrategyText(s).description}>
                      {ringStrategyText(s).label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <SectionHeading>Who takes the calls</SectionHeading>

            <LineSkillsField
              value={form.watch("required_skills")}
              onChange={(next) => form.setValue("required_skills", next, { shouldDirty: true })}
              tenantId={campaign.tenant_id}
              enabled={open}
              relaxSeconds={form.watch("skill_relax_seconds")}
            >
              {/* Shown whatever the line requires of its own: the relax time lifts a number's
                  skills and a handoff's `closer` for this line's callers too. */}
              <Field
                label="Lift the required skills after"
                htmlFor="line-skill-relax"
                error={errors.skill_relax_seconds?.message}
                hint={
                  <Hint more="A stage rule that decides a caller brings its own wait and replaces this one for them: no wait of its own means they hold out for the rule's reps. On soft language routing a Spanish lead only prefers a Spanish rep, so there is no requirement here to lift.">
                    Seconds a caller waits for a rep with the skills the call needs — the line's, the
                    number's, Spanish for a Spanish lead on hard language routing, and a handoff's
                    closer — before any rep on the line may answer. 0 never lifts them, except a
                    handoff, which lifts on its own wait.
                  </Hint>
                }
              >
                <Input
                  type="number"
                  inputMode="numeric"
                  invalid={Boolean(errors.skill_relax_seconds)}
                  trailing={<Unit zero="never" value={form.watch("skill_relax_seconds")} />}
                  {...form.register("skill_relax_seconds", { valueAsNumber: true })}
                />
              </Field>
            </LineSkillsField>

            {ringStrategy !== "ring_all" && (
              <Field
                label="Who rings first"
                htmlFor="line-skill-strategy"
                hint={
                  skillStrategy === "longest_idle" ? (
                    skillStrategyText(skillStrategy).description
                  ) : (
                    <Hint more="Only the skills the call needs count: the line's, the number's, the handoff's, and Spanish for a Spanish lead. A call that needs none rings the rep free the longest.">
                      {skillStrategyText(skillStrategy).description}
                    </Hint>
                  )
                }
              >
                <Select
                  value={skillStrategy}
                  onValueChange={(v) => form.setValue("skill_strategy", v)}
                >
                  <SelectTrigger id="line-skill-strategy">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SKILL_STRATEGIES.map((s) => (
                      <SelectItem key={s} value={s} description={skillStrategyText(s).description}>
                        {skillStrategyText(s).label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}

            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-medium text-text">Previous rep first</p>
                <p className="text-xs text-text-muted">
                  <Hint more="Skills are not checked. If that rep is busy or away, the call is routed as usual.">
                    A caller who calls back rings their last rep first.
                  </Hint>
                </p>
              </div>
              <Switch
                checked={form.watch("previous_rep_first")}
                onCheckedChange={(v) => form.setValue("previous_rep_first", v, { shouldDirty: true })}
                aria-label="Previous rep first"
              />
            </div>

            <div className="space-y-1">
              <StageRulesField
                value={form.watch("stage_rules")}
                onChange={(rules) =>
                  form.setValue("stage_rules", rules, { shouldDirty: true, shouldValidate: true })
                }
                tenantId={campaign.tenant_id}
                enabled={open}
              />
              {errors.stage_rules?.message && (
                <p role="alert" className="text-xs text-danger-11">{errors.stage_rules.message}</p>
              )}
            </div>

            <SectionHeading>Timing</SectionHeading>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Ring a rep for"
                htmlFor="line-ring"
                error={errors.ring_timeout_seconds?.message}
                hint={
                  ringStrategy === "ring_all"
                    ? "Before the whole line is rung again."
                    : "Before the call goes to another rep."
                }
              >
                <Input
                  type="number"
                  inputMode="numeric"
                  invalid={Boolean(errors.ring_timeout_seconds)}
                  trailing={<Unit zero={undefined} value={form.watch("ring_timeout_seconds")} />}
                  {...form.register("ring_timeout_seconds", { valueAsNumber: true })}
                />
              </Field>
              <Field
                label="Wrap-up"
                htmlFor="line-wrap-up"
                error={errors.wrap_up_seconds?.message}
                hint="Before the rep is offered another call."
              >
                <Input
                  type="number"
                  inputMode="numeric"
                  invalid={Boolean(errors.wrap_up_seconds)}
                  trailing={<Unit zero={"none"} value={form.watch("wrap_up_seconds")} />}
                  {...form.register("wrap_up_seconds", { valueAsNumber: true })}
                />
              </Field>
              <Field
                label="Disposition window"
                htmlFor="line-disposition"
                error={errors.disposition_seconds?.message}
                hint="To choose an outcome after a call."
              >
                <Input
                  type="number"
                  inputMode="numeric"
                  invalid={Boolean(errors.disposition_seconds)}
                  trailing={<Unit zero={"none"} value={form.watch("disposition_seconds")} />}
                  {...form.register("disposition_seconds", { valueAsNumber: true })}
                />
              </Field>
              <Field
                label="Caller waits up to"
                htmlFor="line-max-wait"
                error={errors.max_wait_seconds?.message}
                hint={<Hint more="Then an inbound caller is hung up, or a handoff opens to any rep.">Before the wait ends.</Hint>}
              >
                <Input
                  type="number"
                  inputMode="numeric"
                  invalid={Boolean(errors.max_wait_seconds)}
                  trailing={<Unit zero={"no limit"} value={form.watch("max_wait_seconds")} />}
                  {...form.register("max_wait_seconds", { valueAsNumber: true })}
                />
              </Field>
            </div>

            <SectionHeading>What callers hear</SectionHeading>

            <Field
              label="Entry announcement"
              htmlFor="line-announcement"
              error={errors.entry_announcement?.message}
              hint="Played to a caller before they wait."
            >
              <AudioPicker
                value={form.watch("entry_announcement")}
                onChange={(v) =>
                  form.setValue("entry_announcement", v, { shouldDirty: true, shouldValidate: true })
                }
                emptyLabel="No announcement"
              />
            </Field>
            <Field
              label="What a waiting caller hears"
              htmlFor="line-moh"
              error={errors.moh?.message}
              hint="Until a rep takes the call, and again whenever they are put on hold."
            >
              <HoldMedia
                id="line-moh"
                value={form.watch("moh")}
                onChange={(v) => form.setValue("moh", v, { shouldDirty: true, shouldValidate: true })}
              />
            </Field>

            {primary ? (
              <p className="text-xs text-text-muted">
                The primary line takes calls while the campaign is enabled.
              </p>
            ) : (
              <div className="flex items-center justify-between gap-4 border-t border-border pt-4">
                <div>
                  <p className="text-xs font-medium text-text">Taking calls</p>
                  <p className="text-xs text-text-muted">A line that is off takes no new calls.</p>
                </div>
                <Switch
                  checked={form.watch("enabled")}
                  onCheckedChange={(v) => form.setValue("enabled", v, { shouldDirty: true })}
                  aria-label="Taking calls"
                />
              </div>
            )}
          </DialogBody>

          <DialogFooter className="sm:items-center">
            <p className="min-w-0 text-xs text-text-muted sm:mr-auto sm:line-clamp-2" title={summary} aria-live="polite">
              {summary}
            </p>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={form.formState.isSubmitting}>
              {line ? "Save line" : "Add line"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

/** A heading that splits the line form into parts, with a rule above all but the first. */
function SectionHeading({ children, first }: { children: React.ReactNode; first?: boolean }) {
  return (
    <h3
      className={cn(
        "text-2xs font-semibold uppercase tracking-wide text-text-subtle",
        !first && "border-t border-border pt-4",
      )}
    >
      {children}
    </h3>
  );
}

/** The unit inside a seconds field, or what 0 means there. */
function Unit({ value, zero }: { value: number; zero?: string }) {
  return <span className="pr-1.5 text-xs">{value === 0 && zero ? zero : "sec"}</span>;
}
